import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  Pagination,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
} from "@mui/material";
import { UserTable, UserControls, useUserAnalytics } from "./users";
import useAdminAPIClient from "../../api/admin/hooks/useAdminAPIClient";
import { setAdminUsers } from "../../redux/actions/admin";

const UserAnalytics = ({
  onSwitchTab,
  onSetMessageRecipient,
  onSetTaskSearchTerm,
  initialUserSearchTerm,
  onClearUserSearchTerm,
}) => {
  const dispatch = useDispatch();
  const { users, loading } = useSelector((state) => state.admin);
  const { deleteUser, getUsers, toggleFriend } = useAdminAPIClient();
  const {
    searchTerm,
    sortBy,
    sortOrder,
    page,
    rowsPerPage,
    copyFeedback,
    processedUsers,
    paginatedUsers,
    setSearchTerm,
    setRowsPerPage,
    setPage,
    setCopyFeedback,
    handleSort,
    copyToClipboard,
    getCompletionRate,
    getActivityStatus,
  } = useUserAnalytics(users);

  // Delete user state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteResult, setDeleteResult] = useState(null);

  // Set initial search term from Sessions tab "View user" action
  useEffect(() => {
    if (initialUserSearchTerm) {
      setSearchTerm(initialUserSearchTerm);
      if (onClearUserSearchTerm) {
        onClearUserSearchTerm();
      }
    }
  }, [initialUserSearchTerm, setSearchTerm, onClearUserSearchTerm]);

  const handleMessageUser = (userId) => {
    // Switch to messaging tab (tab "6")
    onSwitchTab("6");
    // Set the message recipient
    onSetMessageRecipient(userId);
  };

  const handleViewTasks = (userId) => {
    // Switch to tasks tab (tab "4")
    onSwitchTab("4");
    // Set the task search term to filter by this user
    if (onSetTaskSearchTerm) {
      onSetTaskSearchTerm(userId);
    }
  };

  const handleDeleteUser = (userId, userName) => {
    setUserToDelete({ userId, userName });
    setDeleteDialogOpen(true);
  };

  const handleToggleFriend = async (userId) => {
    try {
      await toggleFriend(userId);
      // Refresh users to update the user list
      const data = await getUsers();
      if (data?.users) {
        dispatch(setAdminUsers(data.users));
      }
    } catch (error) {
      console.error("Failed to toggle friend status:", error);
    }
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;

    setDeleteLoading(true);
    try {
      const result = await deleteUser(userToDelete.userId);
      setDeleteResult({
        success: true,
        message: `User ${userToDelete.userName || userToDelete.userId} and all associated data deleted successfully`,
        details: result.deletionResults,
      });

      // Refresh users to update the user list
      const data = await getUsers();
      if (data?.users) {
        dispatch(setAdminUsers(data.users));
      }
    } catch (error) {
      setDeleteResult({
        success: false,
        message: `Failed to delete user: ${error.message || "Unknown error"}`,
      });
    } finally {
      setDeleteLoading(true);
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  const handleCloseResultSnackbar = () => {
    setDeleteResult(null);
  };

  if (loading?.users) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 300,
        }}
      >
        <CircularProgress size={64} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Search and Controls */}
      <UserControls
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={setRowsPerPage}
        totalUsers={processedUsers.length}
      />

      {/* Users Table */}
      <UserTable
        users={paginatedUsers}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        onCopyUserId={copyToClipboard}
        onMessageUser={handleMessageUser}
        onViewTasks={handleViewTasks}
        onDeleteUser={handleDeleteUser}
        onToggleFriend={handleToggleFriend}
        getCompletionRate={getCompletionRate}
        getActivityStatus={getActivityStatus}
      />

      {/* Pagination */}
      {processedUsers.length > rowsPerPage && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Pagination
            count={Math.ceil(processedUsers.length / rowsPerPage)}
            page={page}
            onChange={(e, newPage) => setPage(newPage)}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* Copy feedback */}
      <Snackbar
        open={copyFeedback}
        autoHideDuration={2000}
        onClose={() => setCopyFeedback(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setCopyFeedback(false)}
          severity="success"
          variant="filled"
        >
          User ID copied to clipboard!
        </Alert>
      </Snackbar>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="delete-user-dialog-title"
        aria-describedby="delete-user-dialog-description"
      >
        <DialogTitle id="delete-user-dialog-title" sx={{ color: "error.main" }}>
          ⚠️ Delete User Account
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-user-dialog-description">
            <Typography paragraph>
              You are about to <strong>permanently delete</strong> the user{" "}
              <strong>{userToDelete?.userName || userToDelete?.userId}</strong>{" "}
              and all their associated data.
            </Typography>
            <Typography paragraph color="error">
              This action will delete:
            </Typography>
            <Typography component="ul" sx={{ pl: 2, color: "error.main" }}>
              <li>User profile and settings</li>
              <li>All tasks and recurring tasks</li>
              <li>All labels</li>
              <li>All activities</li>
              <li>All feedback submissions</li>
              <li>All messages</li>
              <li>Firebase authentication account</li>
            </Typography>
            <Typography paragraph sx={{ mt: 2, fontWeight: "bold" }}>
              This action cannot be undone.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={20} /> : null}
          >
            {deleteLoading ? "Deleting..." : "Delete User Permanently"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete result snackbar */}
      <Snackbar
        open={Boolean(deleteResult)}
        autoHideDuration={6000}
        onClose={handleCloseResultSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseResultSnackbar}
          severity={deleteResult?.success ? "success" : "error"}
          variant="filled"
        >
          {deleteResult?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserAnalytics;
